import { localMode } from "@/transport/local-mode"
import { controlApiUrl } from "@/transport/control-api"
import { jsonObject, jsonString } from "@/transport/decode"
import { expectJson, requestFetch } from "@/transport/http"

export type AppInfo = {
  role: "control-plane" | "machine"
  accountAuth: boolean
  localMode?: boolean
  installerUrl: string
  frontendVersion?: string
  buildVersion?: string
  /** The control plane offers test accounts without Google (local test deployments only). */
  testSignIn?: boolean
  /** A test deployment's shorter heartbeat window before a machine shows offline. */
  machineOfflineAfterSeconds?: number
}

function decodeAppInfo(value: unknown): AppInfo {
  const data = jsonObject(value, "Server returned invalid application metadata")
  const role = jsonString(data.role, "Server returned invalid application metadata")
  if (role !== "control-plane" && role !== "machine") {
    throw new Error("Server returned an unknown application role")
  }
  if (typeof data.account_auth !== "boolean") {
    throw new Error("Server returned invalid application metadata")
  }
  return {
    role,
    localMode: data.local_mode === true,
    accountAuth: data.account_auth,
    installerUrl: data.installer_url === undefined
      ? ""
      : jsonString(data.installer_url, "Server returned invalid application metadata"),
    ...(data.frontend_version === undefined ? {} : { frontendVersion: jsonString(data.frontend_version, "Server returned invalid application metadata") }),
    ...(data.build_version === undefined ? {} : { buildVersion: jsonString(data.build_version, "Server returned invalid application metadata") }),
    ...(data.test_sign_in === true ? { testSignIn: true } : {}),
    ...(typeof data.machine_offline_after_seconds === "number" && data.machine_offline_after_seconds > 0
      ? { machineOfflineAfterSeconds: data.machine_offline_after_seconds }
      : {}),
  }
}

export function embeddedAppInfo(document: Pick<Document, "getElementById"> = window.document) {
  const value = document.getElementById("mux-app-info")?.textContent?.trim()
  if (!value || value === "__MUX_APP_INFO__") return undefined
  try {
    return decodeAppInfo(JSON.parse(value))
  } catch {
    return undefined
  }
}

export async function getAppInfo(customFetch: typeof fetch = fetch) {
  const embedded = localMode() ? undefined : embeddedAppInfo()
  if (embedded) return embedded
  const response = await requestFetch(controlApiUrl("/api/app-info"), { cache: "no-store" }, customFetch)
  return expectJson(response, decodeAppInfo, { error: "Could not identify this Kettle server" })
}
